import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Clock,
  Play,
  Square,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Hourglass,
  MapPin,
  Loader2,
  Camera,
  Pencil,
  Lock,
  ChevronDown,
  CalendarDays,
} from "lucide-react";
import { getClockSelfieUrl } from "@/utils/clock-token.functions";
import { useServerFn } from "@tanstack/react-start";
import {
  GEOFENCE_RADIUS_METERS,
  assertWithinGeofence,
  getCurrentPosition,
} from "@/lib/geolocation";
import { AbsenceReviewCard } from "@/components/time-tracking/AbsenceReviewCard";
import { UnpaidHoursCard } from "@/components/time-tracking/UnpaidHoursCard";
import { ManualEntryDialog } from "@/components/time-tracking/ManualEntryDialog";
import { EditEntryDialog, type EditableEntry } from "@/components/time-tracking/EditEntryDialog";
import { LocationHistoryDialog } from "@/components/time-tracking/LocationHistoryDialog";
import { useFilterByOwnerCompany } from "@/owner/OwnerCompanyContext";
import { CompanyBadge } from "@/owner/CompanyBadge";
import { formatHoursToHM } from "@/lib/hours-format";
import { buildEntryTitle } from "@/lib/entry-title";

export const Route = createFileRoute("/_app/time-tracking/")({
  component: TimeTrackingPage,
});

type Project = { id: string; code: string; name: string; client_id: string };
type Client = { id: string; name: string };
type JobSite = {
  id: string;
  name: string;
  project_id: string;
  latitude: number | null;
  longitude: number | null;
};
type Profile = { id: string; full_name: string | null; email: string };
type TimeEntry = {
  id: string;
  user_id: string;
  company_id: string;
  project_id: string | null;
  job_site_id: string | null;
  clock_in: string;
  clock_out: string | null;
  break_minutes: number;
  hours: number;
  notes: string | null;
  status: "pending" | "approved" | "rejected";
  approved_by: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  clock_in_gps_denied?: boolean | null;
  clock_out_gps_denied?: boolean | null;
  clock_in_latitude?: number | null;
  clock_out_latitude?: number | null;
  clock_in_selfie_path?: string | null;
  clock_out_selfie_path?: string | null;
  auto_clock_out?: boolean | null;
  clock_out_job_site_id?: string | null;
  projects?: { name: string; code: string } | null;
  job_sites?: { name: string } | null;
};

function startOfWeek(date: Date) {
  const d = new Date(date);
  const day = d.getDay(); // 0 Sun .. 6 Sat
  const diff = day === 0 ? -6 : 1 - day; // Monday start
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}
function endOfWeek(date: Date) {
  const s = startOfWeek(date);
  const e = new Date(s);
  e.setDate(s.getDate() + 7);
  return e;
}
function fmtDate(d: Date) {
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
function fmtRange(s: Date, e: Date) {
  const eVisible = new Date(e);
  eVisible.setDate(e.getDate() - 1);
  return `${s.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${eVisible.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
}
function ymdInTz(date: Date, tz?: string) {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz || undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);
    const y = parts.find((p) => p.type === "year")?.value ?? "0000";
    const m = parts.find((p) => p.type === "month")?.value ?? "01";
    const d = parts.find((p) => p.type === "day")?.value ?? "01";
    return `${y}-${m}-${d}`;
  } catch {
    return date.toISOString().slice(0, 10);
  }
}
function ymdLocal(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function TimeTrackingPage() {
  const { t } = useTranslation();
  const { profile, company, hasAnyRole, hasRole } = useAuth();
  const isSupervisor = hasAnyRole(["admin", "supervisor"]);
  const isAdmin = hasRole("admin");
  const [allEmployees, setAllEmployees] = useState<Profile[]>([]);
  const getSelfieUrl = useServerFn(getClockSelfieUrl);

  const openSelfie = async (path: string) => {
    try {
      const res = (await getSelfieUrl({ data: { path } })) as { url: string };
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("timeTracking.errors.openPhoto"));
    }
  };

  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [jobSites, setJobSites] = useState<JobSite[]>([]);
  const [profilesMap, setProfilesMap] = useState<Record<string, Profile>>({});
  const [activeEntry, setActiveEntry] = useState<TimeEntry | null>(null);
  const [clockCardOpen, setClockCardOpen] = useState(false);
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingEntry, setEditingEntry] = useState<EditableEntry | null>(null);
  const [locationEntryId, setLocationEntryId] = useState<string | null>(null);
  // Map of payroll lock ranges per user: userId -> array of [periodStart, periodEnd, runNumber]
  const [payrollLocks, setPayrollLocks] = useState<
    Record<string, { start: string; end: string; number: string }[]>
  >({});

  // Clock-in form
  const [selClient, setSelClient] = useState<string>("");
  const [selProject, setSelProject] = useState<string>("");
  const [selJobSite, setSelJobSite] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [breakMin, setBreakMin] = useState<number>(0);
  const [gpsLoading, setGpsLoading] = useState(false);

  // Week navigation
  const [weekStart, setWeekStart] = useState<Date>(startOfWeek(new Date()));
  const weekEnd = useMemo(() => endOfWeek(weekStart), [weekStart]);

  // Filter (for supervisors)
  const [filterUser, setFilterUser] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<"all" | "pending" | "approved" | "rejected">(
    "all",
  );
  const [filterJobSite, setFilterJobSite] = useState<string>("all");
  const COLLAPSED_DAYS_KEY = "timesheet:collapsedDays";
  const [collapsedDays, setCollapsedDays] = useState<Record<string, boolean>>(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(COLLAPSED_DAYS_KEY);
      return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(COLLAPSED_DAYS_KEY, JSON.stringify(collapsedDays));
    } catch {
      // ignore quota / serialization errors
    }
  }, [collapsedDays]);

  const toggleDay = (key: string) => setCollapsedDays((prev) => ({ ...prev, [key]: !prev[key] }));

  const loadAll = async () => {
    if (!company?.id || !profile?.id) return;
    setLoading(true);

    const [projRes, clientsRes, sitesRes, activeRes, weekRes] = await Promise.all([
      supabase
        .from("projects")
        .select("id, code, name, client_id")
        .eq("company_id", company.id)
        .order("code"),
      supabase
        .from("clients")
        .select("id, name")
        .eq("company_id", company.id)
        .eq("is_active", true)
        .order("name"),
      supabase
        .from("job_sites")
        .select("id, name, project_id, latitude, longitude")
        .eq("company_id", company.id)
        .eq("is_active", true)
        .order("name"),
      supabase
        .from("time_entries")
        .select("*, projects(name, code), job_sites(name)")
        .eq("user_id", profile.id)
        .is("clock_out", null)
        .order("clock_in", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from("time_entries")
        .select("*, projects(name, code), job_sites(name)")
        .gte("clock_in", weekStart.toISOString())
        .lt("clock_in", weekEnd.toISOString())
        .order("clock_in", { ascending: false }),
    ]);

    setProjects((projRes.data ?? []) as Project[]);
    setClients((clientsRes.data ?? []) as Client[]);
    setJobSites((sitesRes.data ?? []) as JobSite[]);
    setActiveEntry((activeRes.data as TimeEntry | null) ?? null);
    const list = (weekRes.data ?? []) as TimeEntry[];
    setEntries(list);

    // Load profiles for displayed entries (supervisors)
    const userIds = Array.from(new Set(list.map((e) => e.user_id)));
    if (userIds.length > 0) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", userIds);
      const map: Record<string, Profile> = {};
      (profs ?? []).forEach((p) => (map[p.id] = p as Profile));
      setProfilesMap(map);
    }

    if (isAdmin) {
      const { data: allProfs } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .eq("company_id", company.id)
        .eq("is_active", true)
        .order("full_name");
      setAllEmployees((allProfs ?? []) as Profile[]);
    }

    // Load payroll runs that overlap the visible week, to lock entries already in payroll.
    // An entry is locked if there's a payroll_run_item for its user_id whose run's
    // [period_start, period_end] contains the entry's date and status != 'cancelled'.
    const weekStartDate = weekStart.toISOString().slice(0, 10);
    const weekEndDate = new Date(weekEnd.getTime() - 1).toISOString().slice(0, 10);
    const { data: runs } = await supabase
      .from("payroll_runs")
      .select("id, number, period_start, period_end, status, payroll_run_items(user_id)")
      .eq("company_id", company.id)
      .neq("status", "cancelled")
      .lte("period_start", weekEndDate)
      .gte("period_end", weekStartDate);
    const locks: Record<string, { start: string; end: string; number: string }[]> = {};
    (runs ?? []).forEach((r: any) => {
      const items = (r.payroll_run_items ?? []) as { user_id: string }[];
      items.forEach((it) => {
        if (!locks[it.user_id]) locks[it.user_id] = [];
        locks[it.user_id].push({
          start: r.period_start,
          end: r.period_end,
          number: r.number,
        });
      });
    });
    setPayrollLocks(locks);

    setLoading(false);
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, profile?.id, weekStart.getTime()]);

  const filteredProjects = useMemo(
    () => (selClient ? projects.filter((p) => p.client_id === selClient) : []),
    [projects, selClient],
  );

  const filteredJobSites = useMemo(
    () => (selProject ? jobSites.filter((s) => s.project_id === selProject) : []),
    [jobSites, selProject],
  );

  useEffect(() => {
    setSelProject("");
    setSelJobSite("");
  }, [selClient]);

  useEffect(() => {
    setSelJobSite("");
  }, [selProject]);

  const handleClockIn = async () => {
    if (!company?.id || !profile?.id) return;
    if (!selProject) {
      toast.error(t("timeTracking.errors.selectProject"));
      return;
    }
    if (!selJobSite) {
      toast.error(t("timeTracking.errors.selectJobSite"));
      return;
    }
    const site = jobSites.find((s) => s.id === selJobSite);
    if (!site) {
      toast.error(t("timeTracking.errors.siteNotFound"));
      return;
    }
    if (site.latitude == null || site.longitude == null) {
      toast.error(t("timeTracking.errors.noCoords"));
      return;
    }

    setGpsLoading(true);
    try {
      const coords = await getCurrentPosition();
      assertWithinGeofence(coords, {
        latitude: Number(site.latitude),
        longitude: Number(site.longitude),
      });

      const { error } = await supabase.from("time_entries").insert({
        company_id: company.id,
        user_id: profile.id,
        project_id: selProject,
        job_site_id: selJobSite,
        clock_in: new Date().toISOString(),
        clock_in_latitude: coords.latitude,
        clock_in_longitude: coords.longitude,
        clock_in_accuracy: coords.accuracy,
        notes: notes.trim() || null,
      });
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success(t("timeTracking.toasts.clockInOk"));
      setNotes("");
      setSelJobSite("");
      await loadAll();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("timeTracking.errors.clockInFail"));
    } finally {
      setGpsLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!activeEntry) return;

    // Validate GPS against the same job site used at clock-in (if any)
    const site = activeEntry.job_site_id
      ? jobSites.find((s) => s.id === activeEntry.job_site_id)
      : null;

    setGpsLoading(true);
    try {
      let coords: Awaited<ReturnType<typeof getCurrentPosition>> | null = null;
      if (site && site.latitude != null && site.longitude != null) {
        coords = await getCurrentPosition();
        assertWithinGeofence(coords, {
          latitude: Number(site.latitude),
          longitude: Number(site.longitude),
        });
      }

      const { error } = await supabase
        .from("time_entries")
        .update({
          clock_out: new Date().toISOString(),
          break_minutes: breakMin,
          notes: notes.trim() || activeEntry.notes,
          ...(coords
            ? {
                clock_out_latitude: coords.latitude,
                clock_out_longitude: coords.longitude,
                clock_out_accuracy: coords.accuracy,
              }
            : {}),
        })
        .eq("id", activeEntry.id);
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success(t("timeTracking.toasts.clockOutOk"));
      setBreakMin(0);
      setNotes("");
      await loadAll();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("timeTracking.errors.clockOutFail"));
    } finally {
      setGpsLoading(false);
    }
  };

  const updateStatus = async (id: string, status: "approved" | "rejected", reason?: string) => {
    const { error } = await supabase
      .from("time_entries")
      .update({
        status,
        approved_by: profile?.id ?? null,
        approved_at: new Date().toISOString(),
        rejection_reason: status === "rejected" ? (reason ?? "Rejected") : null,
      })
      .eq("id", id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(
      status === "approved" ? t("timeTracking.toasts.approved") : t("timeTracking.toasts.rejected"),
    );
    await loadAll();
  };

  const ownerScopedEntries = useFilterByOwnerCompany(entries);

  const visibleEntries = useMemo(() => {
    let list = ownerScopedEntries;
    if (!isSupervisor) list = list.filter((e) => e.user_id === profile?.id);
    if (isSupervisor && filterUser !== "all") list = list.filter((e) => e.user_id === filterUser);
    if (filterStatus !== "all") list = list.filter((e) => e.status === filterStatus);
    if (filterJobSite !== "all")
      list = list.filter((e) =>
        filterJobSite === "none" ? !e.job_site_id : e.job_site_id === filterJobSite,
      );
    return list;
  }, [ownerScopedEntries, isSupervisor, filterUser, filterStatus, filterJobSite, profile?.id]);

  // Group by day (Mon..Sun) using the company timezone so a clock-in always
  // appears on the calendar day that the employee perceived locally.
  const tz = company?.timezone || undefined;
  const days = useMemo(() => {
    const out: { date: Date; entries: TimeEntry[]; total: number }[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      const dayKey = ymdLocal(d);
      const dayEntries = visibleEntries.filter((e) => ymdInTz(new Date(e.clock_in), tz) === dayKey);
      const total = dayEntries.reduce((sum, e) => sum + Number(e.hours || 0), 0);
      out.push({ date: d, entries: dayEntries, total });
    }
    return out;
  }, [visibleEntries, weekStart, tz]);

  const weekTotal = useMemo(() => days.reduce((s, d) => s + d.total, 0), [days]);

  const pendingForApproval = useMemo(
    () => entries.filter((e) => e.status === "pending" && e.clock_out !== null),
    [entries],
  );

  const uniqueUsers = useMemo(() => {
    const ids = Array.from(new Set(entries.map((e) => e.user_id)));
    return ids.map((id) => profilesMap[id]).filter(Boolean) as Profile[];
  }, [entries, profilesMap]);

  const uniqueJobSites = useMemo(() => {
    const byId = new Map<string, string>();
    let hasNone = false;
    for (const e of entries) {
      if (e.job_site_id && e.job_sites?.name) byId.set(e.job_site_id, e.job_sites.name);
      else if (!e.job_site_id) hasNone = true;
    }
    const list = Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    return { list, hasNone };
  }, [entries]);

  // Returns the payroll run number that locks this entry, or null.
  const getEntryLock = (entry: TimeEntry): string | null => {
    const userLocks = payrollLocks[entry.user_id];
    if (!userLocks || userLocks.length === 0) return null;
    const entryDate = ymdInTz(new Date(entry.clock_in), tz);
    const lock = userLocks.find((l) => entryDate >= l.start && entryDate <= l.end);
    return lock ? lock.number : null;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("timeTracking.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("timeTracking.subtitle")}</p>
        </div>
      </div>

      {/* Clock card */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader
          className="cursor-pointer select-none"
          onClick={() => setClockCardOpen((v) => !v)}
          role="button"
          aria-expanded={clockCardOpen}
        >
          <CardTitle className="flex items-center gap-2 text-lg">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-500/15">
              <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </span>
            {activeEntry ? t("timeTracking.currentlyClockedIn") : t("timeTracking.clockIn")}
            {activeEntry && (
              <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-emerald-500" />
            )}
            <ChevronDown
              className={`ml-auto h-5 w-5 shrink-0 text-blue-500 transition-transform ${
                clockCardOpen ? "" : "-rotate-90"
              }`}
            />
          </CardTitle>
        </CardHeader>
        {clockCardOpen && (
          <CardContent>
            {activeEntry ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-border bg-muted/40 p-4">
                  <p className="text-sm text-muted-foreground">{t("timeTracking.startedAt")}</p>
                  <p className="text-lg font-semibold">
                    {new Date(activeEntry.clock_in).toLocaleString()}
                  </p>
                  {(() => {
                    const title = buildEntryTitle(
                      activeEntry.projects,
                      activeEntry.job_sites,
                      t("timeTracking.noProject"),
                    );
                    return (
                      <>
                        <p className="mt-1 text-sm">{title.primary}</p>
                        {title.secondary && (
                          <p className="text-xs text-muted-foreground">{title.secondary}</p>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="break">{t("timeTracking.breakLabel")}</Label>
                    <Input
                      id="break"
                      type="number"
                      min={0}
                      value={breakMin}
                      onChange={(e) => setBreakMin(Number(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="notes-out">{t("timeTracking.notesLabel")}</Label>
                    <Input
                      id="notes-out"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder={t("timeTracking.notesPlaceholder")}
                    />
                  </div>
                </div>
                <Button onClick={handleClockOut} className="gap-2" disabled={gpsLoading}>
                  {gpsLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                  {gpsLoading ? t("timeTracking.validatingGps") : t("timeTracking.clockOut")}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <Label>{t("timeTracking.client")}</Label>
                    <Select value={selClient} onValueChange={setSelClient}>
                      <SelectTrigger>
                        <SelectValue placeholder={t("timeTracking.selectClient")} />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("timeTracking.project")}</Label>
                    <Select value={selProject} onValueChange={setSelProject} disabled={!selClient}>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            selClient
                              ? t("timeTracking.selectProject")
                              : t("timeTracking.selectClientFirst")
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredProjects.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.code} — {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>
                      {t("timeTracking.jobSite")} <span className="text-destructive">*</span>
                    </Label>
                    <Select value={selJobSite} onValueChange={setSelJobSite} disabled={!selProject}>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            selProject
                              ? t("timeTracking.selectJobSite")
                              : t("timeTracking.selectProjectFirst")
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredJobSites.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                            {s.latitude == null || s.longitude == null
                              ? ` ${t("timeTracking.noGps")}`
                              : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span>{t("timeTracking.geofenceHint", { radius: GEOFENCE_RADIUS_METERS })}</span>
                </div>
                <div>
                  <Label htmlFor="notes-in">{t("timeTracking.notesLabel")}</Label>
                  <Textarea
                    id="notes-in"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={t("timeTracking.notesInPlaceholder")}
                    rows={2}
                  />
                </div>
                <Button
                  onClick={handleClockIn}
                  className="gap-2"
                  disabled={gpsLoading || !selJobSite}
                >
                  {gpsLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  {gpsLoading ? t("timeTracking.validatingGps") : t("timeTracking.doClockIn")}
                </Button>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Weekly timesheet */}
      <Card className="border-l-4 border-l-violet-500">
        <CardHeader
          className="flex flex-row items-start justify-between gap-4 cursor-pointer select-none"
          onClick={() => setWeeklyOpen((v) => !v)}
          role="button"
          aria-expanded={weeklyOpen}
        >
          <div className="flex items-start gap-2">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-500/15">
              <CalendarDays className="h-5 w-5 text-violet-600 dark:text-violet-400" />
            </span>
            <div>
              <CardTitle className="text-lg">{t("timeTracking.weekly.title")}</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">{fmtRange(weekStart, weekEnd)}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {weeklyOpen && (
              <div
                className="flex flex-wrap items-center gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                {isAdmin && (
                  <ManualEntryDialog
                    projects={projects}
                    clients={clients}
                    jobSites={jobSites}
                    employees={allEmployees}
                    companyTimezone={company?.timezone}
                    onCreated={loadAll}
                  />
                )}
                {isSupervisor && uniqueUsers.length > 0 && (
                  <Select value={filterUser} onValueChange={setFilterUser}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder={t("timeTracking.weekly.allEmployees")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("timeTracking.weekly.allEmployees")}</SelectItem>
                      {uniqueUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.full_name ?? u.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Select
                  value={filterStatus}
                  onValueChange={(v) => setFilterStatus(v as typeof filterStatus)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("timeTracking.weekly.allStatuses", { defaultValue: "Todos os status" })}
                    </SelectItem>
                    <SelectItem value="pending">
                      {t("timeTracking.weekly.statusPending")}
                    </SelectItem>
                    <SelectItem value="approved">
                      {t("timeTracking.weekly.statusApproved")}
                    </SelectItem>
                    <SelectItem value="rejected">
                      {t("timeTracking.weekly.statusRejected")}
                    </SelectItem>
                  </SelectContent>
                </Select>
                {(uniqueJobSites.list.length > 0 || uniqueJobSites.hasNone) && (
                  <Select value={filterJobSite} onValueChange={setFilterJobSite}>
                    <SelectTrigger className="w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        {t("timeTracking.weekly.allJobSites", { defaultValue: "Todas as obras" })}
                      </SelectItem>
                      {uniqueJobSites.list.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                      {uniqueJobSites.hasNone && (
                        <SelectItem value="none">
                          {t("timeTracking.weekly.noJobSite", { defaultValue: "Sem obra" })}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    const d = new Date(weekStart);
                    d.setDate(d.getDate() - 7);
                    setWeekStart(d);
                  }}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setWeekStart(startOfWeek(new Date()))}
                >
                  {t("timeTracking.weekly.thisWeek")}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    const d = new Date(weekStart);
                    d.setDate(d.getDate() + 7);
                    setWeekStart(d);
                  }}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
            <ChevronDown
              className={`h-5 w-5 shrink-0 text-violet-500 transition-transform ${
                weeklyOpen ? "" : "-rotate-90"
              }`}
            />
          </div>
        </CardHeader>
        {weeklyOpen && (
          <CardContent>
            <div className="mb-2 flex items-center justify-between rounded-lg border border-violet-200 bg-gradient-to-r from-violet-500/10 via-blue-500/10 to-transparent p-3 dark:border-violet-900">
              <span className="text-sm font-medium">{t("timeTracking.weekly.totalThisWeek")}</span>
              <span className="text-2xl font-bold text-violet-700 dark:text-violet-300">
                {formatHoursToHM(weekTotal)}
              </span>
            </div>

            {/* Legenda das cores de status das linhas */}
            <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm border border-amber-500/50 bg-amber-400/30" />
                {t("timeTracking.weekly.legendInProgress", { defaultValue: "Em andamento" })}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm border border-blue-500/40 bg-blue-500/20" />
                {t("timeTracking.weekly.statusApproved")}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm border border-red-500/40 bg-red-500/20" />
                {t("timeTracking.weekly.statusRejected")}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm border border-border bg-card" />
                {t("timeTracking.weekly.statusPending")}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm border border-muted-foreground/30 bg-muted" />
                {t("timeTracking.weekly.legendInPayroll", {
                  defaultValue: "Em folha (bloqueado)",
                })}
              </span>
            </div>

            {loading ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {t("timeTracking.weekly.loading")}
              </p>
            ) : (
              <div className="space-y-4">
                {days.map((day) => {
                  const dayKey = day.date.toISOString();
                  const isCollapsed = collapsedDays[dayKey] ?? false;
                  return (
                    <div key={dayKey} className="rounded-lg border border-border">
                      <button
                        type="button"
                        onClick={() => toggleDay(dayKey)}
                        className="flex w-full items-center justify-between border-b border-border bg-muted/30 px-4 py-2 text-left transition-colors hover:bg-muted/50"
                        aria-expanded={!isCollapsed}
                      >
                        <div className="flex items-center gap-2">
                          <ChevronDown
                            className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                              isCollapsed ? "-rotate-90" : ""
                            }`}
                          />
                          <span className="text-sm font-semibold">{fmtDate(day.date)}</span>
                        </div>
                        <span className="text-sm font-medium">{formatHoursToHM(day.total)}</span>
                      </button>
                      {!isCollapsed &&
                        (day.entries.length === 0 ? (
                          <p className="px-4 py-3 text-xs text-muted-foreground">
                            {t("timeTracking.weekly.noEntries")}
                          </p>
                        ) : (
                          <ul className="divide-y divide-border">
                            {day.entries.map((e) => {
                              const lockNumber = getEntryLock(e);
                              const isLocked = lockNumber !== null;
                              // Fundo por status: em andamento (sem saída) = amarelo,
                              // aprovado = azul claro, rejeitado = vermelho claro.
                              const statusBg =
                                e.clock_out === null
                                  ? "bg-amber-400/20"
                                  : e.status === "approved"
                                    ? "bg-blue-500/10"
                                    : e.status === "rejected"
                                      ? "bg-red-500/10"
                                      : "";
                              return (
                                <li
                                  key={e.id}
                                  className={`flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${
                                    isLocked ? "bg-muted/40 opacity-70" : statusBg
                                  }`}
                                >
                                  <div className="min-w-0">
                                    {(() => {
                                      const title = buildEntryTitle(
                                        e.projects,
                                        e.job_sites,
                                        t("timeTracking.noProject"),
                                      );
                                      return (
                                        <>
                                          <p
                                            className={`truncate text-sm font-medium ${isLocked ? "text-muted-foreground" : ""}`}
                                          >
                                            {title.primary}
                                          </p>
                                          {title.secondary && (
                                            <p className="truncate text-xs text-muted-foreground">
                                              {title.secondary}
                                            </p>
                                          )}
                                        </>
                                      );
                                    })()}
                                    {(() => {
                                      if (
                                        !e.clock_out_job_site_id ||
                                        e.clock_out_job_site_id === e.job_site_id
                                      )
                                        return null;
                                      const outName = jobSites.find(
                                        (s) => s.id === e.clock_out_job_site_id,
                                      )?.name;
                                      if (!outName) return null;
                                      return (
                                        <p className="mt-0.5 truncate text-xs text-amber-600 dark:text-amber-500">
                                          {t("timeTracking.clockedOutAt", { site: outName })}
                                        </p>
                                      );
                                    })()}
                                    {/* Horário · intervalo · funcionário, numa "pastilha" para
                                        identificar a pessoa de relance. É só visual (span, não
                                        button): destaca sem prometer um clique que não existe.
                                        Pulsa apenas quando o lançamento pede ação (pendente ou
                                        em andamento), para o movimento apontar onde olhar em vez
                                        de virar ruído. `motion-safe:` respeita quem pediu menos
                                        animação no sistema. */}
                                    <span
                                      className={`mt-1 inline-flex max-w-full items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                                        !isLocked && (e.status === "pending" || !e.clock_out)
                                          ? "border-primary/30 bg-primary/10 text-foreground motion-safe:animate-pulse"
                                          : "border-border bg-muted text-muted-foreground"
                                      }`}
                                    >
                                      {new Date(e.clock_in).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        timeZone: company?.timezone || undefined,
                                      })}{" "}
                                      –{" "}
                                      {e.clock_out
                                        ? new Date(e.clock_out).toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            timeZone: company?.timezone || undefined,
                                          })
                                        : t("timeTracking.weekly.inProgress")}
                                      {e.break_minutes > 0
                                        ? ` · ${t("timeTracking.weekly.breakSuffix", { minutes: e.break_minutes })}`
                                        : ""}
                                      {isSupervisor && profilesMap[e.user_id]
                                        ? ` · ${profilesMap[e.user_id].full_name ?? profilesMap[e.user_id].email}`
                                        : ""}
                                    </span>
                                    {e.notes && (
                                      <p className="mt-1 text-xs text-muted-foreground">
                                        {e.notes}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span
                                      className={`text-sm font-semibold tabular-nums ${isLocked ? "text-muted-foreground" : ""}`}
                                    >
                                      {formatHoursToHM(Number(e.hours))}
                                    </span>
                                    <CompanyBadge companyId={e.company_id} />
                                    <StatusBadge status={e.status} />
                                    {isLocked && (
                                      <Badge
                                        variant="outline"
                                        className="gap-1 border-muted-foreground/40 bg-muted text-muted-foreground"
                                        title={t("timeTracking.weekly.inPayrollTitle", {
                                          number: lockNumber,
                                        })}
                                      >
                                        <Lock className="h-3 w-3" />{" "}
                                        {t("timeTracking.weekly.inPayroll", { number: lockNumber })}
                                      </Badge>
                                    )}
                                    {(e.clock_in_gps_denied || e.clock_out_gps_denied) &&
                                      (() => {
                                        // "Não confirmada" = registrado por selfie SEM nenhuma
                                        // coordenada (GPS indisponível e sem EXIF). Diferente de
                                        // "sem GPS do navegador mas localizado pela foto".
                                        const unconfirmed =
                                          (e.clock_in_gps_denied && e.clock_in_latitude == null) ||
                                          (e.clock_out_gps_denied && e.clock_out_latitude == null);
                                        return (
                                          <Badge
                                            variant="outline"
                                            className={
                                              unconfirmed
                                                ? "border-amber-500/50 text-amber-600 dark:text-amber-400 gap-1"
                                                : "border-destructive/40 text-destructive gap-1"
                                            }
                                          >
                                            <Camera className="h-3 w-3" />{" "}
                                            {unconfirmed
                                              ? t("timeTracking.weekly.reviewNoLocationBadge")
                                              : t("timeTracking.weekly.noGpsBadge")}
                                          </Badge>
                                        );
                                      })()}
                                    {e.auto_clock_out && (
                                      <Badge
                                        variant="destructive"
                                        className="gap-1"
                                        title="Saída registrada automaticamente após 10h"
                                      >
                                        <Clock className="h-3 w-3" /> Saída auto
                                      </Badge>
                                    )}
                                    {isSupervisor && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 gap-1"
                                        onClick={() => setLocationEntryId(e.id)}
                                      >
                                        <MapPin className="h-3.5 w-3.5" />
                                        Localizações
                                      </Button>
                                    )}
                                    {isSupervisor && e.clock_in_selfie_path && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 gap-1"
                                        onClick={() => openSelfie(e.clock_in_selfie_path!)}
                                      >
                                        <Camera className="h-3.5 w-3.5" />
                                        {t("timeTracking.weekly.photoIn")}
                                      </Button>
                                    )}
                                    {isSupervisor && e.clock_out_selfie_path && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 gap-1"
                                        onClick={() => openSelfie(e.clock_out_selfie_path!)}
                                      >
                                        <Camera className="h-3.5 w-3.5" />
                                        {t("timeTracking.weekly.photoOut")}
                                      </Button>
                                    )}
                                    {isAdmin && !isLocked && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 gap-1"
                                        onClick={() =>
                                          setEditingEntry({
                                            id: e.id,
                                            user_id: e.user_id,
                                            project_id: e.project_id,
                                            job_site_id: e.job_site_id,
                                            clock_in: e.clock_in,
                                            clock_out: e.clock_out,
                                            break_minutes: e.break_minutes,
                                            notes: e.notes,
                                            status: e.status,
                                            rejection_reason: e.rejection_reason,
                                          })
                                        }
                                      >
                                        <Pencil className="h-3.5 w-3.5" />
                                        {t("timeTracking.weekly.edit")}
                                      </Button>
                                    )}
                                    {isSupervisor &&
                                      !isLocked &&
                                      e.status === "pending" &&
                                      e.clock_out !== null && (
                                        <>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7 gap-1"
                                            onClick={() => updateStatus(e.id, "approved")}
                                          >
                                            <CheckCircle2 className="h-3.5 w-3.5" />
                                            {t("timeTracking.weekly.approve")}
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7 gap-1 text-destructive"
                                            onClick={() => {
                                              const reason =
                                                window.prompt(
                                                  t("timeTracking.weekly.rejectionPrompt"),
                                                ) ?? "";
                                              if (reason !== null)
                                                updateStatus(e.id, "rejected", reason);
                                            }}
                                          >
                                            <XCircle className="h-3.5 w-3.5" />
                                            {t("timeTracking.weekly.reject")}
                                          </Button>
                                        </>
                                      )}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        ))}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {isSupervisor && pendingForApproval.length > 0 && (
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader
            className="cursor-pointer select-none"
            onClick={() => setPendingOpen((v) => !v)}
            role="button"
            aria-expanded={pendingOpen}
          >
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/15">
                <Hourglass className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </span>
              {t("timeTracking.pendingApproval.title", { count: pendingForApproval.length })}
              <ChevronDown
                className={`ml-auto h-5 w-5 shrink-0 text-amber-500 transition-transform ${
                  pendingOpen ? "" : "-rotate-90"
                }`}
              />
            </CardTitle>
          </CardHeader>
          {pendingOpen && (
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {t("timeTracking.pendingApproval.hint")}
              </p>
            </CardContent>
          )}
        </Card>
      )}

      {isSupervisor && (
        <UnpaidHoursCard
          profilesMap={Object.fromEntries(
            Object.entries(profilesMap).map(([id, p]) => [
              id,
              { full_name: p.full_name, email: p.email },
            ]),
          )}
        />
      )}

      {isSupervisor && (
        <AbsenceReviewCard
          profilesMap={Object.fromEntries(
            Object.entries(profilesMap).map(([id, p]) => [
              id,
              { full_name: p.full_name, email: p.email },
            ]),
          )}
        />
      )}

      <p className="text-center text-xs text-muted-foreground">
        {t("timeTracking.footer.needAdd")}{" "}
        <Link to="/projects" className="text-primary hover:underline">
          {t("timeTracking.footer.manageProjects")}
        </Link>
      </p>

      {isAdmin && (
        <EditEntryDialog
          entry={editingEntry}
          projects={projects}
          clients={clients}
          jobSites={jobSites}
          open={editingEntry !== null}
          onOpenChange={(open) => !open && setEditingEntry(null)}
          onSaved={loadAll}
          companyTimezone={company?.timezone}
          employeeName={
            editingEntry?.user_id
              ? (profilesMap[editingEntry.user_id]?.full_name ??
                profilesMap[editingEntry.user_id]?.email ??
                null)
              : null
          }
        />
      )}

      <LocationHistoryDialog
        entryId={locationEntryId}
        open={locationEntryId !== null}
        onOpenChange={(o) => !o && setLocationEntryId(null)}
        timezone={company?.timezone}
      />
    </div>
  );
}

function StatusBadge({ status }: { status: "pending" | "approved" | "rejected" }) {
  const { t } = useTranslation();
  if (status === "approved") {
    return (
      <Badge variant="outline" className="border-primary/40 text-primary">
        {t("timeTracking.weekly.statusApproved")}
      </Badge>
    );
  }
  if (status === "rejected") {
    return (
      <Badge variant="outline" className="border-destructive/40 text-destructive">
        {t("timeTracking.weekly.statusRejected")}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-muted-foreground/40 text-muted-foreground">
      {t("timeTracking.weekly.statusPending")}
    </Badge>
  );
}
